import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '@/utils/api';
import { 
  ArrowLeft, 
  Building2, 
  Upload, 
  Palette, 
  Save,
  User,
  Mail,
  DollarSign,
  Users,
  Image as ImageIcon,
  Check,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  Bell,
  Megaphone,
  LayoutDashboard,
  Mic,
  Brain,
  Smile,
  Camera,
  FileText,
  Link2,
  Trophy,
  Flame,
  Award,
  Share2,
  Moon,
  Eye,
  TrendingUp,
  Copy,
  Globe,
  Sparkles,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  BookOpen,
  Heart,
  Target,
  Star,
  Video,
  Loader2,
  GripVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Footer from '@/components/layout/Footer';

const API_BASE = API.replace('/api', '');

// Password strength validation
const validatePassword = (password) => {
  const errors = [];
  if (password.length < 8) errors.push('at least 8 characters');
  if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
  if (!/[a-z]/.test(password)) errors.push('one lowercase letter');
  if (!/[0-9]/.test(password)) errors.push('one number');
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('one special character');
  return errors;
};

const getPasswordStrength = (password) => {
  if (!password) return { level: 0, label: '', color: '' };
  const errors = validatePassword(password);
  const score = 5 - errors.length;
  if (score <= 1) return { level: 1, label: 'Weak', color: '#ef4444' };
  if (score <= 2) return { level: 2, label: 'Fair', color: '#f97316' };
  if (score <= 3) return { level: 3, label: 'Good', color: '#eab308' };
  if (score <= 4) return { level: 4, label: 'Strong', color: '#22c55e' };
  return { level: 5, label: 'Very Strong', color: '#10b981' };
};

const NewClientPage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [createdClient, setCreatedClient] = useState(null); // Store created client credentials
  const [customMoodUploading, setCustomMoodUploading] = useState(false);
  const [customMoodPreview, setCustomMoodPreview] = useState(null);
  const [pdfMoodUploading, setPdfMoodUploading] = useState(false);
  const [pdfMoodPreview, setPdfMoodPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    seats: 10,
    price_per_seat: 29.99,
    admin_username: '',
    admin_password: '',
    logo_url: '',
    app_name: 'Journal',
    ai_coach_name: 'Your Journal Coach',
    features: {
      // Core Journal Features
      ai_coach: true,
      voice_typing: true,
      mood_tracking: true,
      image_attachments: true,
      pdf_export: true,
      link_preview: true,
      // Gamification
      gamification: true,
      streaks: true,
      badges: true,
      // Promotional & Marketing
      sidebar_promo: true,
      main_promo_banner: true,
      push_notifications: true,
      referral_system: true,
      video_embeds: true,
      // Appearance
      themes: true,
      dark_mode: true,
      // Premium Features
      client_admin_portal: true,
      analytics_dashboard: true,
      // Branding Control (for white-label)
      show_powered_by: true,
      show_upgrade_prompts: true
    },
    // FULL 8-COLOR THEME (matching EditClientPage)
    theme: {
      brand_color: '#3B82F6',           // Buttons, links, highlights
      background_color: '#0F172A',      // Main page background
      card_color: '#1E293B',            // Cards, panels, sections
      text_color: '#F8FAFC',            // Primary text
      border_color: '#475569',          // Card borders, dividers
      accent_color: '#22D3EE',          // Highlights, badges
      secondary_text_color: '#94A3B8',  // Muted, helper text
      input_background: '#1E293B'       // Form fields
    },
    // Custom Branded Mood
    custom_mood_enabled: false,
    custom_mood_image_url: '',
    custom_mood_label: '',
    custom_mood_pdf_image_url: '',
    // White-label customization
    custom_domain: '',
    // Footer customization
    company_name: '',
    privacy_policy_url: '',
    terms_url: '',
    support_email: '',
    copyright_text: ''
  });

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
    
    // Upload
    setLogoUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const res = await axios.post(`${API_BASE}/api/upload/image`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setFormData({ ...formData, logo_url: res.data.url });
      toast.success('Logo uploaded successfully');
    } catch (err) {
      console.error('Logo upload failed:', err);
      toast.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  // Handle custom mood icon upload
  const handleCustomMoodUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type (SVG or PNG)
    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an SVG, PNG, JPG, or WebP image');
      return;
    }
    
    // Validate file size (max 1MB for mood icon)
    if (file.size > 1024 * 1024) {
      toast.error('Custom mood image must be under 1MB');
      return;
    }
    
    setCustomMoodUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API_BASE}/api/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setFormData(prev => ({ ...prev, custom_mood_image_url: res.data.url }));
      setCustomMoodPreview(res.data.url);
      toast.success('Custom mood image uploaded');
    } catch (error) {
      console.error('Custom mood upload error:', error);
      toast.error('Failed to upload custom mood image');
    } finally {
      setCustomMoodUploading(false);
    }
  };

  // Handle PDF mood image upload (server-hosted for PDF generation)
  const handlePdfMoodUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an SVG, PNG, JPG, or WebP image');
      return;
    }
    
    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      toast.error('PDF mood image must be under 1MB');
      return;
    }
    
    setPdfMoodUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API_BASE}/api/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setFormData(prev => ({ ...prev, custom_mood_pdf_image_url: res.data.url }));
      setPdfMoodPreview(res.data.url);
      toast.success('PDF mood image uploaded');
    } catch (error) {
      console.error('PDF mood upload error:', error);
      toast.error('Failed to upload PDF mood image');
    } finally {
      setPdfMoodUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.contact_email || !formData.admin_username || !formData.admin_password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Strong password validation
    const passwordErrors = validatePassword(formData.admin_password);
    if (passwordErrors.length > 0) {
      toast.error(`Password must have: ${passwordErrors.join(', ')}`);
      return;
    }
    
    setSaving(true);
    try {
      const clientData = {
        ...formData
      };
      
      await axios.post(`${API_BASE}/api/admin/clients`, clientData, {
        withCredentials: true
      }).then(res => {
        // Store the created client with credentials to show in modal
        setCreatedClient({
          name: res.data.client.name,
          client_id: res.data.client.client_id,
          admin_username: res.data.client.admin_username,
          portal_password: res.data.client.portal_password,
          contact_email: res.data.client.contact_email
        });
        toast.success('Client created successfully!');
      });
    } catch (err) {
      console.error('Failed to create client:', err);
      toast.error(err.response?.data?.detail || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D063E] to-[#1a0527] flex flex-col">
      <div className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with Logo */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin?tab=b2b')}
                className="p-2 rounded-lg bg-[#4A0A66]/50 text-white hover:bg-[#4A0A66] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Building2 className="w-7 h-7 text-[#0FFFE2]" />
                  Create New B2B Client
                </h1>
                <p className="text-[#FDF2F8]/60 text-sm mt-1">
                  Set up a new white-label client with custom branding
                </p>
              </div>
            </div>
            {/* Logo */}
            <img 
              src="/new-logo-homepage.png" 
              alt="UnCloned" 
              className="h-10 w-auto hidden md:block"
            />
          </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#df1283]" />
              Client Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                  Company Name <span className="text-[#df1283]">*</span>
                </label>
                <Input
                  data-testid="new-client-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Zenith Wellness"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                  Contact Email <span className="text-[#df1283]">*</span>
                </label>
                <Input
                  data-testid="new-client-email-input"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="contact@company.com"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  required
                />
              </div>
            </div>
          </div>

          {/* Logo Upload */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#FF7901]" />
              Client Logo
            </h2>
            <div className="flex items-start gap-6">
              {/* Logo Preview */}
              <div className="w-32 h-32 rounded-xl bg-[#4A0A66]/30 border-2 border-dashed border-[#4A0A66] flex items-center justify-center overflow-hidden">
                {logoPreview || formData.logo_url ? (
                  <img 
                    src={logoPreview || formData.logo_url} 
                    alt="Logo preview" 
                    className="w-full h-full object-contain p-2"
                  />
                ) : (
                  <ImageIcon className="w-10 h-10 text-[#FDF2F8]/30" />
                )}
              </div>
              
              {/* Upload Button */}
              <div className="flex-1">
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/50"
                    disabled={logoUploading}
                    onClick={(e) => e.target.closest('label').querySelector('input').click()}
                  >
                    {logoUploading ? (
                      <>Uploading...</>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Logo
                      </>
                    )}
                  </Button>
                </label>
                <p className="text-xs text-[#FDF2F8]/50 mt-2">
                  SVG or PNG with transparent background. Any aspect ratio supported.
                </p>
                {formData.logo_url && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-[#22c55e]">
                    <Check className="w-4 h-4" />
                    Logo uploaded
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Licensing */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#22c55e]" />
              Licensing
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                  Number of Seats
                </label>
                <Input
                  type="number"
                  min="1"
                  value={formData.seats}
                  onChange={(e) => setFormData({ ...formData, seats: parseInt(e.target.value) || 1 })}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                  Price per Seat ($)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_per_seat}
                  onChange={(e) => setFormData({ ...formData, price_per_seat: parseFloat(e.target.value) || 0 })}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
            </div>
            <div className="mt-4 p-3 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg">
              <div className="text-sm text-[#22c55e]">
                Estimated Revenue: <span className="font-bold">${(formData.seats * formData.price_per_seat).toFixed(2)}/month</span>
              </div>
            </div>
          </div>

          {/* Admin Access */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0FFFE2]" />
              Client Portal Admin
            </h2>
            <p className="text-sm text-[#FDF2F8]/60 mb-4">
              These credentials will be used by the client to access their admin portal
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                  Admin Username <span className="text-[#df1283]">*</span>
                </label>
                <Input
                  data-testid="new-client-admin-username-input"
                  value={formData.admin_username}
                  onChange={(e) => setFormData({ ...formData, admin_username: e.target.value })}
                  placeholder="e.g., zenith-admin"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                  Admin Password <span className="text-[#df1283]">*</span>
                </label>
                <Input
                  data-testid="new-client-admin-password-input"
                  type="password"
                  value={formData.admin_password}
                  onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                  placeholder="Strong password required"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  required
                />
                {/* Password strength indicator */}
                {formData.admin_password && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-1.5 bg-[#4A0A66]/50 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-300"
                          style={{ 
                            width: `${(getPasswordStrength(formData.admin_password).level / 5) * 100}%`,
                            backgroundColor: getPasswordStrength(formData.admin_password).color
                          }}
                        />
                      </div>
                      <span 
                        className="text-xs font-medium"
                        style={{ color: getPasswordStrength(formData.admin_password).color }}
                      >
                        {getPasswordStrength(formData.admin_password).label}
                      </span>
                    </div>
                    {validatePassword(formData.admin_password).length > 0 && (
                      <p className="text-xs text-[#FDF2F8]/50">
                        Missing: {validatePassword(formData.admin_password).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Feature Toggles */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ToggleLeft className="w-5 h-5 text-[#0FFFE2]" />
              Feature Access
            </h2>
            <p className="text-sm text-[#FDF2F8]/60 mb-6">
              Enable or disable specific features for this client's white-label experience
            </p>
            
            {/* Core Journal Features */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-[#0FFFE2] mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Core Journal Features
              </h3>
              <div className="grid md:grid-cols-3 gap-3">
                {[
                  { key: 'ai_coach', label: 'AI Coach', desc: 'AI-powered reflections & insights', icon: Brain },
                  { key: 'voice_typing', label: 'Voice Typing', desc: 'Speech-to-text journaling', icon: Mic },
                  { key: 'mood_tracking', label: 'Mood Tracking', desc: 'Daily mood selection & analytics', icon: Smile },
                  { key: 'image_attachments', label: 'Image Attachments', desc: 'Upload photos to entries', icon: Camera },
                  { key: 'pdf_export', label: 'PDF Export', desc: 'Download entries as PDF', icon: FileText },
                  { key: 'link_preview', label: 'Link Preview', desc: 'Rich URL previews in entries', icon: Link2 }
                ].map(({ key, label, desc, icon: Icon }) => (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.features[key] 
                        ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                        : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                    }`}
                    onClick={() => setFormData({
                      ...formData,
                      features: { ...formData.features, [key]: !formData.features[key] }
                    })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                        <div>
                          <div className="font-medium text-white text-sm">{label}</div>
                          <div className="text-xs text-[#FDF2F8]/50">{desc}</div>
                        </div>
                      </div>
                      {formData.features[key] ? (
                        <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gamification */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-[#FF7901] mb-3 flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                Gamification
              </h3>
              <div className="grid md:grid-cols-3 gap-3">
                {[
                  { key: 'gamification', label: 'Gamification', desc: 'Enable all game elements', icon: Trophy },
                  { key: 'streaks', label: 'Streaks', desc: 'Daily journaling streaks', icon: Flame },
                  { key: 'badges', label: 'Badges', desc: 'Achievement badges & rewards', icon: Award }
                ].map(({ key, label, desc, icon: Icon }) => (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.features[key] 
                        ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                        : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                    }`}
                    onClick={() => setFormData({
                      ...formData,
                      features: { ...formData.features, [key]: !formData.features[key] }
                    })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                        <div>
                          <div className="font-medium text-white text-sm">{label}</div>
                          <div className="text-xs text-[#FDF2F8]/50">{desc}</div>
                        </div>
                      </div>
                      {formData.features[key] ? (
                        <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Promotional & Marketing */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-[#df1283] mb-3 flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                Promotional & Marketing
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { key: 'sidebar_promo', label: 'Sidebar Promo Videos', desc: 'Rotating motivational videos', icon: Megaphone },
                  { key: 'main_promo_banner', label: 'Promo Banner Carousel', desc: 'Dashboard promotional banners', icon: ImageIcon },
                  { key: 'push_notifications', label: 'Push Notifications', desc: 'In-app notification system', icon: Bell },
                  { key: 'referral_system', label: 'Referral System', desc: 'User referral program', icon: Share2 },
                  { key: 'video_embeds', label: 'Video Embeds', desc: 'Custom embedded videos for users', icon: Video }
                ].map(({ key, label, desc, icon: Icon }) => (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.features[key] 
                        ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                        : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                    }`}
                    onClick={() => setFormData({
                      ...formData,
                      features: { ...formData.features, [key]: !formData.features[key] }
                    })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                        <div>
                          <div className="font-medium text-white text-sm">{label}</div>
                          <div className="text-xs text-[#FDF2F8]/50">{desc}</div>
                        </div>
                      </div>
                      {formData.features[key] ? (
                        <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Appearance */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-[#6366F1] mb-3 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Appearance
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { key: 'themes', label: 'Theme Customization', desc: 'User-customizable themes', icon: Palette },
                  { key: 'dark_mode', label: 'Dark/Light Mode', desc: 'Toggle between modes', icon: Moon }
                ].map(({ key, label, desc, icon: Icon }) => (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.features[key] 
                        ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                        : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                    }`}
                    onClick={() => setFormData({
                      ...formData,
                      features: { ...formData.features, [key]: !formData.features[key] }
                    })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                        <div>
                          <div className="font-medium text-white text-sm">{label}</div>
                          <div className="text-xs text-[#FDF2F8]/50">{desc}</div>
                        </div>
                      </div>
                      {formData.features[key] ? (
                        <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Features */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-[#fbbf24] mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Premium Features
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { key: 'client_admin_portal', label: 'Client Admin Portal', desc: 'Self-serve management dashboard', icon: LayoutDashboard },
                  { key: 'analytics_dashboard', label: 'Analytics Dashboard', desc: 'User analytics & insights', icon: TrendingUp }
                ].map(({ key, label, desc, icon: Icon }) => (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.features[key] 
                        ? 'bg-[#22c55e]/10 border-[#22c55e]/50' 
                        : 'bg-[#4A0A66]/20 border-[#4A0A66]'
                    }`}
                    onClick={() => setFormData({
                      ...formData,
                      features: { ...formData.features, [key]: !formData.features[key] }
                    })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${formData.features[key] ? 'text-[#22c55e]' : 'text-[#FDF2F8]/40'}`} />
                        <div>
                          <div className="font-medium text-white text-sm">{label}</div>
                          <div className="text-xs text-[#FDF2F8]/50">{desc}</div>
                        </div>
                      </div>
                      {formData.features[key] ? (
                        <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* White-Label Customization */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#FF7901]" />
              White-Label Customization
            </h2>
            <p className="text-sm text-[#FDF2F8]/60 mb-4">
              Custom domain and branding for a complete white-label experience
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">App Name (shown in header)</label>
                <Input
                  value={formData.app_name}
                  onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                  placeholder="Journal"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">AI Coach Name</label>
                <Input
                  value={formData.ai_coach_name}
                  onChange={(e) => setFormData({ ...formData, ai_coach_name: e.target.value })}
                  placeholder="Your Journal Coach"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Custom Domain (optional)</label>
                <Input
                  value={formData.custom_domain}
                  onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
                  placeholder="journal.clientcompany.com"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Support Email</label>
                <Input
                  value={formData.support_email}
                  onChange={(e) => setFormData({ ...formData, support_email: e.target.value })}
                  placeholder="support@clientcompany.com"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
            </div>

            {/* DNS Configuration Guide */}
            {formData.custom_domain && (
              <div className="bg-[#4A0A66]/20 rounded-lg p-4 border border-[#4A0A66]/50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-[#0FFFE2] flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    DNS Configuration Instructions
                  </h3>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/30"
                    onClick={() => {
                      const instructions = `DNS Configuration for ${formData.custom_domain}\n\nTo connect your custom domain, add the following DNS record:\n\nRecord Type: CNAME\nHost/Name: ${formData.custom_domain.split('.')[0] || 'journal'}\nValue/Points to: app.unclonedjournal.com\nTTL: 3600 (or "Auto")\n\nAlternatively, if using root domain:\n\nRecord Type: A\nHost/Name: @\nValue: Contact UnCloned for IP address\n\nNote: DNS changes can take up to 48 hours to propagate.`;
                      navigator.clipboard.writeText(instructions);
                      toast.success('DNS instructions copied to clipboard!');
                    }}
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy Instructions
                  </Button>
                </div>
                <div className="space-y-3 text-sm">
                  <p className="text-[#FDF2F8]/60">
                    Send these DNS settings to your client's IT team:
                  </p>
                  <div className="bg-[#1a0527] rounded-lg p-3 font-mono text-xs">
                    <div className="grid grid-cols-[80px_1fr] gap-2">
                      <span className="text-[#0FFFE2]">Type:</span>
                      <span className="text-white">CNAME</span>
                      <span className="text-[#0FFFE2]">Host:</span>
                      <span className="text-white">{formData.custom_domain.split('.')[0] || 'journal'}</span>
                      <span className="text-[#0FFFE2]">Value:</span>
                      <span className="text-[#FF7901]">app.unclonedjournal.com</span>
                      <span className="text-[#0FFFE2]">TTL:</span>
                      <span className="text-white">3600</span>
                    </div>
                  </div>
                  <p className="text-[#FDF2F8]/40 text-xs">
                    Note: DNS changes can take up to 48 hours to propagate. Once configured, contact UnCloned support to verify the domain connection.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer & Legal Links */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#0FFFE2]" />
              Footer & Legal Links
            </h2>
            <p className="text-sm text-[#FDF2F8]/60 mb-4">
              Customize footer content for the client's branding
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Company Name (footer)</label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Client Company Inc."
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Copyright Text</label>
                <Input
                  value={formData.copyright_text}
                  onChange={(e) => setFormData({ ...formData, copyright_text: e.target.value })}
                  placeholder="© 2026 Client Company. All rights reserved."
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Privacy Policy URL</label>
                <Input
                  value={formData.privacy_policy_url}
                  onChange={(e) => setFormData({ ...formData, privacy_policy_url: e.target.value })}
                  placeholder="https://clientcompany.com/privacy"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Terms of Service URL</label>
                <Input
                  value={formData.terms_url}
                  onChange={(e) => setFormData({ ...formData, terms_url: e.target.value })}
                  placeholder="https://clientcompany.com/terms"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
            </div>
          </div>

          {/* White-Label Branding Control */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#FF7901]/50 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#FF7901]" />
              White-Label Branding Control
            </h2>
            <p className="text-sm text-[#FDF2F8]/60 mb-4">
              Turn these OFF to create a fully white-labeled experience with no UnCloned references
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Show Powered By */}
              <div 
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  formData.features.show_powered_by 
                    ? 'bg-[#4A0A66]/20 border-[#4A0A66]' 
                    : 'bg-[#ef4444]/10 border-[#ef4444]/50'
                }`}
                onClick={() => setFormData({
                  ...formData,
                  features: { ...formData.features, show_powered_by: !formData.features.show_powered_by }
                })}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-white text-sm">"Powered by UnCloned"</div>
                    <div className="text-xs text-[#FDF2F8]/50">Shows in client portal footer</div>
                  </div>
                  {formData.features.show_powered_by ? (
                    <ToggleRight className="w-6 h-6 text-[#22c55e]" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-[#ef4444]" />
                  )}
                </div>
              </div>

              {/* Show Upgrade Prompts */}
              <div 
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  formData.features.show_upgrade_prompts 
                    ? 'bg-[#4A0A66]/20 border-[#4A0A66]' 
                    : 'bg-[#ef4444]/10 border-[#ef4444]/50'
                }`}
                onClick={() => setFormData({
                  ...formData,
                  features: { ...formData.features, show_upgrade_prompts: !formData.features.show_upgrade_prompts }
                })}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-white text-sm">Upgrade Prompts</div>
                    <div className="text-xs text-[#FDF2F8]/50">Hide upgrade banners for users</div>
                  </div>
                  {formData.features.show_upgrade_prompts ? (
                    <ToggleRight className="w-6 h-6 text-[#22c55e]" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-[#ef4444]" />
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-[#FDF2F8]/40 mt-4 italic">
              Tip: Turn both OFF for a fully white-labeled experience
            </p>
          </div>

          {/* Theme Colors */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5 text-[#df1283]" />
              Brand Colors
            </h2>
            <p className="text-sm text-[#FDF2F8]/60 mb-4">
              Customize the 8 colors for your client's white-labeled portal
            </p>
            
            {/* Core Colors */}
            <p className="text-xs text-[#FDF2F8]/50 uppercase tracking-wider mb-3">Core Colors</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { key: 'brand_color', label: 'Brand Color', desc: 'Buttons, links' },
                { key: 'background_color', label: 'Background', desc: 'Page background' },
                { key: 'card_color', label: 'Card Color', desc: 'Panels, sections' },
                { key: 'text_color', label: 'Text Color', desc: 'Primary text' }
              ].map(({ key, label, desc }) => (
                <div key={key}>
                  <label className="block text-sm text-[#FDF2F8]/70 mb-2">{label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.theme[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, [key]: e.target.value }
                      })}
                      className="w-10 h-10 rounded-full cursor-pointer bg-transparent border-2 border-[#4A0A66] appearance-none [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0"
                    />
                    <Input
                      value={formData.theme[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, [key]: e.target.value }
                      })}
                      className="bg-[#4A0A66]/30 border-[#4A0A66] text-white text-xs flex-1"
                    />
                  </div>
                  <p className="text-xs text-[#FDF2F8]/40 mt-1">{desc}</p>
                </div>
              ))}
            </div>
            
            {/* Additional Colors */}
            <p className="text-xs text-[#FDF2F8]/50 uppercase tracking-wider mb-3">Additional Colors</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'border_color', label: 'Border Color', desc: 'Card borders, dividers' },
                { key: 'accent_color', label: 'Accent Color', desc: 'Highlights, badges' },
                { key: 'secondary_text_color', label: 'Secondary Text', desc: 'Muted, helper text' },
                { key: 'input_background', label: 'Input Background', desc: 'Form fields' }
              ].map(({ key, label, desc }) => (
                <div key={key}>
                  <label className="block text-sm text-[#FDF2F8]/70 mb-2">{label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.theme[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, [key]: e.target.value }
                      })}
                      className="w-10 h-10 rounded-full cursor-pointer bg-transparent border-2 border-[#4A0A66] appearance-none [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0"
                    />
                    <Input
                      value={formData.theme[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, [key]: e.target.value }
                      })}
                      className="bg-[#4A0A66]/30 border-[#4A0A66] text-white text-xs flex-1"
                    />
                  </div>
                  <p className="text-xs text-[#FDF2F8]/40 mt-1">{desc}</p>
                </div>
              ))}
            </div>
            
            {/* Theme Preview */}
            <div className="mt-6 p-4 rounded-lg border" style={{ backgroundColor: formData.theme.background_color, borderColor: formData.theme.border_color }}>
              <p className="text-sm mb-2" style={{ color: formData.theme.secondary_text_color }}>Preview:</p>
              <div className="flex items-center gap-3 flex-wrap">
                <button 
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: formData.theme.brand_color, color: formData.theme.text_color }}
                >
                  Button
                </button>
                <div 
                  className="px-4 py-2 rounded-lg border"
                  style={{ backgroundColor: formData.theme.card_color, borderColor: formData.theme.border_color }}
                >
                  <span style={{ color: formData.theme.text_color }}>Card</span>
                </div>
                <input 
                  type="text"
                  placeholder="Input field"
                  readOnly
                  className="px-3 py-2 rounded-lg text-sm border"
                  style={{ 
                    backgroundColor: formData.theme.input_background, 
                    borderColor: formData.theme.border_color,
                    color: formData.theme.text_color
                  }}
                />
                <span 
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{ backgroundColor: formData.theme.accent_color, color: formData.theme.background_color }}
                >
                  Accent
                </span>
              </div>
            </div>
          </div>

          {/* Custom Branded Mood - Premium Feature */}
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#FF7901]/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#FF7901]" />
                  Custom Branded Mood
                  <span className="text-xs bg-[#FF7901]/20 text-[#FF7901] px-2 py-0.5 rounded-full">Premium</span>
                </h2>
                <p className="text-sm text-[#FDF2F8]/60 mt-1">
                  Replace the default "UnCloned®" mood with this client's own branded mood option
                </p>
              </div>
              <div 
                data-testid="new-client-custom-mood-toggle"
                role="switch"
                aria-checked={formData.custom_mood_enabled}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                  formData.custom_mood_enabled 
                    ? 'bg-[#22c55e]/20 border border-[#22c55e]/50' 
                    : 'bg-[#4A0A66]/30 border border-[#4A0A66]'
                }`}
                onClick={() => setFormData(prev => ({ ...prev, custom_mood_enabled: !prev.custom_mood_enabled }))}
              >
                <span className="text-sm text-white">{formData.custom_mood_enabled ? 'Enabled' : 'Disabled'}</span>
                {formData.custom_mood_enabled ? (
                  <ToggleRight className="w-5 h-5 text-[#22c55e]" />
                ) : (
                  <ToggleLeft className="w-5 h-5 text-[#FDF2F8]/30" />
                )}
              </div>
            </div>

            {formData.custom_mood_enabled ? (
              <div className="space-y-4">
                {/* Preview */}
                <div className="bg-[#1a0527] rounded-xl p-4 border border-[#4A0A66]">
                  <p className="text-xs text-[#FDF2F8]/50 mb-3">Preview (colors will inherit client's theme)</p>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-20 h-20 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: formData.theme?.card_color || '#1E293B' }}
                    >
                      {customMoodPreview ? (
                        <img 
                          src={customMoodPreview} 
                          alt="Custom mood" 
                          className="w-12 h-12 object-contain"
                        />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-[#FDF2F8]/30" />
                      )}
                    </div>
                    <div>
                      <p 
                        className="text-lg font-medium"
                        style={{ color: formData.theme?.text_color || '#F8FAFC' }}
                      >
                        {formData.custom_mood_label || 'Custom Mood'}
                      </p>
                      <p className="text-xs text-[#FDF2F8]/50">This will appear in the mood picker</p>
                    </div>
                  </div>
                </div>

                {/* Upload and Label */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                      Mood Icon (SVG or PNG)
                    </label>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-16 h-16 rounded-lg border-2 border-dashed border-[#4A0A66] flex items-center justify-center bg-[#4A0A66]/20 overflow-hidden"
                      >
                        {customMoodPreview ? (
                          <img src={customMoodPreview} alt="Mood icon" className="w-10 h-10 object-contain" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-[#FDF2F8]/30" />
                        )}
                      </div>
                      <div className="space-y-2">
                        <input
                          data-testid="new-client-custom-mood-file-input"
                          type="file"
                          accept="image/svg+xml,image/png,image/jpeg,image/webp"
                          onChange={handleCustomMoodUpload}
                          className="hidden"
                          id="custom-mood-upload"
                        />
                        <label htmlFor="custom-mood-upload">
                          <Button
                            type="button"
                            variant="outline"
                            className="border-[#FF7901] text-[#FF7901] cursor-pointer"
                            disabled={customMoodUploading}
                            asChild
                          >
                            <span>
                              {customMoodUploading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              {customMoodUploading ? 'Uploading...' : (customMoodPreview ? 'Change' : 'Upload')}
                            </span>
                          </Button>
                        </label>
                        {customMoodPreview && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 text-xs"
                            onClick={() => {
                              setCustomMoodPreview(null);
                              setFormData(prev => ({ ...prev, custom_mood_image_url: '' }));
                            }}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[#FDF2F8]/40 mt-2">SVG recommended. Max 1MB.</p>
                  </div>

                  {/* Label Input */}
                  <div>
                    <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                      Mood Label
                    </label>
                    <Input
                      data-testid="new-client-custom-mood-label-input"
                      value={formData.custom_mood_label}
                      onChange={(e) => setFormData(prev => ({ ...prev, custom_mood_label: e.target.value }))}
                      placeholder="e.g., Oh Yeah!, Inspired, Branded"
                      className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                      maxLength={20}
                    />
                    <p className="text-xs text-[#FDF2F8]/40 mt-2">
                      {formData.custom_mood_label.length}/20 characters
                    </p>
                  </div>
                </div>

                {/* PDF Mood Image Upload */}
                <div className="mt-4 pt-4 border-t border-[#4A0A66]">
                  <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                    PDF Mood Image (Optional)
                  </label>
                  <p className="text-xs text-[#FDF2F8]/40 mb-3">
                    Upload a server-hosted version of your mood icon for PDF exports. This ensures the mood icon displays correctly in exported PDFs.
                  </p>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-16 h-16 rounded-lg border-2 border-dashed border-[#0FFFE2]/50 flex items-center justify-center bg-[#0FFFE2]/10 overflow-hidden"
                    >
                      {pdfMoodPreview ? (
                        <img src={pdfMoodPreview} alt="PDF Mood icon" className="w-10 h-10 object-contain" />
                      ) : (
                        <FileText className="w-6 h-6 text-[#0FFFE2]/30" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <input
                        type="file"
                        accept="image/svg+xml,image/png,image/jpeg,image/webp"
                        onChange={handlePdfMoodUpload}
                        className="hidden"
                        id="pdf-mood-upload"
                      />
                      <label htmlFor="pdf-mood-upload">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-[#0FFFE2] text-[#0FFFE2] cursor-pointer"
                          disabled={pdfMoodUploading}
                          asChild
                        >
                          <span>
                            {pdfMoodUploading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            {pdfMoodUploading ? 'Uploading...' : (pdfMoodPreview ? 'Change' : 'Upload for PDF')}
                          </span>
                        </Button>
                      </label>
                      {pdfMoodPreview && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 text-xs"
                          onClick={() => {
                            setPdfMoodPreview(null);
                            setFormData(prev => ({ ...prev, custom_mood_pdf_image_url: '' }));
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-[#FF7901]/10 border border-[#FF7901]/30 rounded-lg p-3">
                  <p className="text-xs text-[#FF7901]">
                    <strong>Note:</strong> This custom mood will replace the "UnCloned®" option only in this client's B2B portal. 
                    The main app remains unaffected.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-[#1a0527] rounded-xl p-6 border border-[#4A0A66] text-center">
                <div className="w-12 h-12 rounded-full bg-[#4A0A66]/30 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-[#FDF2F8]/30" />
                </div>
                <p className="text-[#FDF2F8]/50 text-sm">
                  Custom branded mood is disabled for this client.
                </p>
                <p className="text-[#FDF2F8]/30 text-xs mt-2">
                  Enable to let this client have their own branded mood option in the mood picker.
                </p>
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex items-center justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin?tab=b2b')}
              className="border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/50"
            >
              Cancel
            </Button>
            <Button
              data-testid="new-client-submit-button"
              type="submit"
              disabled={saving}
              className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90 px-8"
            >
              {saving ? (
                <>Creating...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Create Client
                </>
              )}
            </Button>
          </div>
        </form>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
      
      {/* Credentials Modal - Shows after successful client creation */}
      {createdClient && (
        <div data-testid="new-client-success-modal" className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl p-8 max-w-lg w-full border border-[#4A0A66] shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-[#df1283] to-[#FF7901] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Client Created Successfully!</h2>
              <p className="text-[#FDF2F8]/60">Save these credentials - the password won't be shown again</p>
            </div>
            
            <div className="bg-[#1a0527]/50 rounded-xl p-6 space-y-4 border border-[#4A0A66]/50">
              <div>
                <label className="text-[#FDF2F8]/50 text-xs uppercase tracking-wider">Company Name</label>
                <p className="text-white font-medium text-lg">{createdClient.name}</p>
              </div>
              
              <div>
                <label className="text-[#FDF2F8]/50 text-xs uppercase tracking-wider">Portal Login URL</label>
                <div className="flex items-center gap-2">
                  <code className="text-[#0FFFE2] font-mono text-sm bg-[#0FFFE2]/10 px-3 py-2 rounded flex-1">
                    {window.location.origin}/client-portal
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/client-portal`);
                      toast.success('URL copied!');
                    }}
                    className="text-[#0FFFE2] hover:bg-[#0FFFE2]/10"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[#FDF2F8]/50 text-xs uppercase tracking-wider">Client ID / Username</label>
                  <div className="flex items-center gap-2">
                    <code className="text-[#FF7901] font-mono bg-[#FF7901]/10 px-3 py-2 rounded flex-1 text-sm">
                      {createdClient.admin_username}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(createdClient.admin_username);
                        toast.success('Username copied!');
                      }}
                      className="text-[#FF7901] hover:bg-[#FF7901]/10"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div>
                  <label className="text-[#FDF2F8]/50 text-xs uppercase tracking-wider">Password</label>
                  <div className="flex items-center gap-2">
                    <code className="text-[#df1283] font-mono bg-[#df1283]/10 px-3 py-2 rounded flex-1 text-sm">
                      {createdClient.portal_password}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(createdClient.portal_password);
                        toast.success('Password copied!');
                      }}
                      className="text-[#df1283] hover:bg-[#df1283]/10"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="text-[#FDF2F8]/50 text-xs uppercase tracking-wider">Contact Email</label>
                <p className="text-white">{createdClient.contact_email}</p>
              </div>
            </div>
            
            <div className="mt-6 flex gap-3">
              <Button
                onClick={() => {
                  const text = `Client Portal Credentials for ${createdClient.name}\n\nPortal URL: ${window.location.origin}/client-portal\nClient ID: ${createdClient.admin_username}\nPassword: ${createdClient.portal_password}`;
                  navigator.clipboard.writeText(text);
                  toast.success('All credentials copied!');
                }}
                className="flex-1 bg-[#4A0A66] hover:bg-[#4A0A66]/80"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All
              </Button>
              <Button
                onClick={() => navigate('/admin?tab=b2b')}
                className="flex-1 bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewClientPage;
